import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "./cn.js";

/**
 * An address the operator has to give to something else — a relay, a player, a
 * console — so it is shown in full and copied in one press. It is never truncated
 * into an ellipsis a person would have to reconstruct by hand.
 */
export function CopyableUrl({
  className,
  label,
  value,
}: {
  className?: string;
  label?: string;
  value: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => {
      setCopied(false);
    }, 1600);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-sm border border-border bg-bg-2 py-1.5 pr-1.5 pl-3",
        className,
      )}
    >
      <span className="min-w-0 flex-1 font-mono text-xs break-all text-ink-2">
        {value}
      </span>
      <button
        aria-label={copied ? "Copied" : `Copy ${label ?? "address"}`}
        className="grid size-8 shrink-0 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-surface-hi hover:text-ink"
        onClick={() => {
          void copyText(value).then((ok) => {
            setCopied(ok);
          });
        }}
        title={copied ? "Copied" : "Copy"}
        type="button"
      >
        {copied ? (
          <Check aria-hidden="true" className="size-3.5 text-ok" />
        ) : (
          <Copy aria-hidden="true" className="size-3.5" />
        )}
      </button>
    </div>
  );
}

/**
 * The clipboard API needs a secure context, which a plain-HTTP deployment is not. The
 * fallback is the old selection copy — it is not elegant, and an operator who cannot
 * copy the address of the thing he is publishing has a worse problem than inelegance.
 */
async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const field = document.createElement("textarea");
      field.value = value;
      field.setAttribute("readonly", "");
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.append(field);
      field.select();
      const copied = document.execCommand("copy");
      field.remove();
      return copied;
    } catch {
      return false;
    }
  }
}
